import React, { useMemo, useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import BusinessOutlinedIcon from "@mui/icons-material/BusinessOutlined";
import { signIn, hasValidSession, refreshAuthSession, refreshSessionIfTenantPending, getTenantIdFromSession } from "../../common/auth";
import { createTenant } from "../../common/tenantApi";
import { slugify } from "./StepCuenta";
import {
  DEFAULT_SECTOR_IPA,
  DEFAULT_EMPLOYEE_COUNT,
  SECTOR_IPA_OPTIONS,
  EMPLOYEE_COUNT_OPTIONS,
} from "./workspaceOptions";
import { FISCAL_IVA_OPTIONS, formatCuitInput, validateFiscalProfileForm } from "../../constants/fiscalProfile";
import { readLandingLead } from "../../common/landingLead";
import { trackEvent } from "../../hooks/useTrackEvent";

const PLANES = [
  { id: "starter", nombre: "Starter", precio: "$0", docs: "500 docs/mes", badge: "Gratis" },
  { id: "growth", nombre: "Growth", precio: "Consultar", docs: "5.000 docs/mes" },
  { id: "enterprise", nombre: "Enterprise", precio: "Custom", docs: "Sin límite" },
];

export default function StepWorkspace() {
  const navigate = useNavigate();
  const profile = useMemo(() => {
    try {
      return JSON.parse(sessionStorage.getItem("onboarding_profile") || "{}");
    } catch {
      return {};
    }
  }, []);

  const [orgName, setOrgName] = useState(() => readLandingLead().empresa || "");
  const [sectorIpa, setSectorIpa] = useState(DEFAULT_SECTOR_IPA);
  const [employeeCount, setEmployeeCount] = useState(DEFAULT_EMPLOYEE_COUNT);
  const [monthlyInvoiceVolume, setMonthlyInvoiceVolume] = useState("");
  const [plan, setPlan] = useState(() => {
    const leadPlan = readLandingLead().plan;
    return PLANES.some((p) => p.id === leadPlan) ? leadPlan : "starter";
  });
  const [fiscalCuit, setFiscalCuit] = useState("");
  const [fiscalLegalName, setFiscalLegalName] = useState("");
  const [fiscalIvaCondition, setFiscalIvaCondition] = useState("RI");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const slug = orgName ? slugify(orgName) : "";

  useEffect(() => {
    if (!profile.email) {
      navigate("/onboarding/cuenta", { replace: true });
      return;
    }
    const confirmed = sessionStorage.getItem("onboarding_email_confirmed") === "true";
    if (!confirmed) {
      hasValidSession().then((ok) => {
        if (!ok) navigate("/onboarding/cuenta", { replace: true });
      });
    }
  }, [profile.email, navigate]);

  const handleSubmit = async () => {
    setLoading(true);
    setError("");
    try {
      if (!orgName.trim()) throw new Error("Ingresá el nombre de la organización");
      if (!sectorIpa) throw new Error("Seleccioná el sector");
      if (!employeeCount) throw new Error("Seleccioná el tamaño del equipo");
      const volume = Number.parseInt(String(monthlyInvoiceVolume).trim(), 10);
      if (!Number.isFinite(volume) || volume < 1) {
        throw new Error("Ingresá el volumen estimado de facturas por mes (≥ 1)");
      }

      const fiscalError = validateFiscalProfileForm({
        fiscalCuit,
        fiscalLegalName,
        fiscalIvaCondition,
      });
      if (fiscalError) throw new Error(fiscalError);

      const email = profile.email;
      const password = sessionStorage.getItem("onboarding_password");
      if (!email) throw new Error("Volvé al paso de cuenta para ingresar email");

      const alreadySignedIn = await hasValidSession();
      if (!alreadySignedIn) {
        if (!password) {
          throw new Error("Sesión expirada — volvé al paso de cuenta e ingresá el código de verificación");
        }
        await signIn(email, password);
        sessionStorage.setItem("onboarding_email_confirmed", "true");
      }

      const { tenant } = await createTenant({
        name: orgName.trim(),
        slug,
        email,
        plan,
        sector_ipa: sectorIpa,
        employee_count: employeeCount,
        monthly_invoice_volume: volume,
        fiscal_cuit: fiscalCuit,
        fiscal_legal_name: fiscalLegalName.trim(),
        fiscal_iva_condition: fiscalIvaCondition,
      });

      sessionStorage.setItem("onboarding_tenant_id", tenant.tenant_id);
      sessionStorage.setItem("onboarding_tenant_name", tenant.name);
      if (tenant.ingest_email) {
        sessionStorage.setItem("onboarding_ingest_email", tenant.ingest_email);
      }

      trackEvent("onboarding_step_2_workspace_created", {
        event_category: "onboarding",
        event_label: "tenant_provisioned",
        step_number: 2,
        tenant_id: tenant.tenant_id,
        company_name: orgName.trim(),
        cuit: fiscalCuit,
      });

      if (password) {
        await refreshAuthSession(email, password);
      } else {
        await refreshSessionIfTenantPending().catch(() => {});
      }

      const claimTenantId = await getTenantIdFromSession().catch(() => null);
      if (claimTenantId && claimTenantId !== tenant.tenant_id && claimTenantId !== "pending") {
        console.warn("JWT tenant_id no coincide con el workspace creado");
      }

      navigate("/onboarding/contacto");
    } catch (e) {
      if (e.code === "UserNotConfirmedException") {
        setError("Confirmá tu email antes de continuar. Volvé al paso Cuenta e ingresá el código.");
        return;
      }
      setError(e.message || "No se pudo crear el workspace");
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <div className="ob-card-header">
        <div className="ob-step-icon">
          <BusinessOutlinedIcon sx={{ fontSize: 24 }} />
        </div>
        <div className="ob-card-header-text">
          <h2 className="ob-step-title">Configurá tu workspace</h2>
          <p className="ob-step-desc">Organización, datos fiscales y plan inicial.</p>
        </div>
      </div>

      <div className="ob-field">
        <label>Organización</label>
        <input
          placeholder="Estudio García & Asoc."
          value={orgName}
          onChange={(e) => setOrgName(e.target.value)}
        />
        {slug && <div className="ob-slug-preview">tagnize.app/{slug}</div>}
      </div>

      <div className="ob-field-row">
        <div className="ob-field">
          <label>Sector</label>
          <select value={sectorIpa} onChange={(e) => setSectorIpa(e.target.value)}>
            {SECTOR_IPA_OPTIONS.map((opt) => (
              <option key={opt} value={opt}>
                {opt}
              </option>
            ))}
          </select>
        </div>
        <div className="ob-field">
          <label>Tamaño del equipo</label>
          <select value={employeeCount} onChange={(e) => setEmployeeCount(e.target.value)}>
            {EMPLOYEE_COUNT_OPTIONS.map((opt) => (
              <option key={opt.value} value={opt.value}>
                {opt.label}
              </option>
            ))}
          </select>
        </div>
      </div>

      <div className="ob-field">
        <label>Volumen estimado de facturas / mes</label>
        <input
          type="number"
          min={1}
          step={1}
          placeholder="Ej. 200"
          value={monthlyInvoiceVolume}
          onChange={(e) => setMonthlyInvoiceVolume(e.target.value)}
          inputMode="numeric"
          required
        />
      </div>

      <div className="ob-field">
        <label>CUIT de la organización</label>
        <input
          placeholder="20-27009484-9"
          value={fiscalCuit}
          onChange={(e) => setFiscalCuit(formatCuitInput(e.target.value))}
          inputMode="numeric"
          required
        />
      </div>
      <div className="ob-field">
        <label>Razón social</label>
        <input
          placeholder="Mi Empresa SRL"
          value={fiscalLegalName}
          onChange={(e) => setFiscalLegalName(e.target.value)}
          required
        />
      </div>
      <div className="ob-field">
        <label>Condición ante IVA</label>
        <select value={fiscalIvaCondition} onChange={(e) => setFiscalIvaCondition(e.target.value)} required>
          {FISCAL_IVA_OPTIONS.map((opt) => (
            <option key={opt.value} value={opt.value}>{opt.label}</option>
          ))}
        </select>
      </div>
      <p className="ob-step-desc">
        Estos datos identifican a tu organización como receptor en facturas de proveedores
        y se guardan en el perfil de la empresa. Al crear el workspace, actualizamos el prompt
        de facturas con tu CUIT y razón social.
      </p>

      <div className="ob-plan-grid">
        {PLANES.map((p) => (
          <div
            key={p.id}
            className={`ob-plan-card ${plan === p.id ? "selected" : ""}`}
            onClick={() => setPlan(p.id)}
            role="button"
            tabIndex={0}
            onKeyDown={(e) => e.key === "Enter" && setPlan(p.id)}
          >
            <div className="plan-name">{p.nombre}</div>
            <div className="plan-price">{p.precio}</div>
            <div className="plan-docs">{p.docs}</div>
            {p.badge && <span className="plan-badge">{p.badge}</span>}
          </div>
        ))}
      </div>

      {error && <p className="ob-error">{error}</p>}

      <button className="ob-btn-primary" onClick={handleSubmit} disabled={loading} type="button">
        {loading ? "Creando workspace..." : "Continuar →"}
      </button>
    </>
  );
}
